import { DateService } from "@app/services/date.service";
import { IJourney } from "@interfaces/IJourney";
import { TranslationService } from "@services/translation.service";
import { VeriDate } from "@services/veriDate.service";
import { VideoStreamType } from "../enums/VideoStreamType";
import { ICameraFootageTimelineDisplaySection, ITimelineSummary } from "../models/CameraFootageTimeline.interfaces";
import { IAvailableDailyFootage } from "../models/IAvailableDailyFootage";
import { IVideoEntry } from "../models/IVideoEntry";

export interface IAvailableVideoFootageSection {
    startTime: moment.Moment;
    endTime: moment.Moment;

    startPercentage: number;
    widthPercentage: number;

    label?: string;
    requested?: boolean;
    videoStreamTypes?: VideoStreamType[];
    historicalFootage?: IAvailableDailyFootage[] | IJourney[];

    hasMainStream?: boolean;
    hasSubStream?: boolean;
}

export class RequestVideoService {

    static $inject = ["veriDate", "dateService", "translationService"];

    constructor(
        private readonly veriDate: VeriDate,
        private readonly dateService: DateService,
        private readonly translationService: TranslationService
    ) {
    }

    createTimelineUsingAAMP(requestDate: Date, footageHistory: IAvailableDailyFootage[]): IAvailableVideoFootageSection[] {
        if (!footageHistory?.length) {
            return;
        }

        const label: string = this.translationService.t("cameraService.Online");

        return this.createTimelineFootage(requestDate, footageHistory, "Start", "End", label);
    }

    createTimelineUsingJourneys(requestDate: Date, journeys: IJourney[]): IAvailableVideoFootageSection[] {
        if (!journeys?.length) {
            return;
        }

        const label: string = this.translationService.t("requestVideo.ignitionOn");

        return this.createTimelineFootage(requestDate, journeys, "StartUtc", "EndUtc", label);
    }

    hasRequestedVideoAtMoment(moment: moment.Moment, videoEntries: IVideoEntry[]): boolean {
        const hoveredExistingRequestedVideo: IVideoEntry = videoEntries?.find(videoEntry => {
            const requestedVideoStartDate = this.veriDate.createMoment(videoEntry.VideoStartTime);
            const requestedVideoEndDate = this.veriDate.createMoment(videoEntry.VideoEndTime);

            if (!moment.isSame(requestedVideoStartDate, "day")) {
                return false;
            }

            return requestedVideoStartDate.isSameOrBefore(moment, "second") && requestedVideoEndDate.isSameOrAfter(moment, "second");
        });

        return !!hoveredExistingRequestedVideo;
    }

    hasFootageAvailableAtTime(time: Date, selectedStreamType: VideoStreamType, timeline: ITimelineSummary): boolean {
        const availableFootage: ICameraFootageTimelineDisplaySection[] = this.getFootageAvailableAtTime(time, timeline)?.filter(section => section.availableVideoStreamTypes.includes(selectedStreamType));

        return !!availableFootage?.length;
    }


    getFootageAvailableAtTime(time: Date, timeline: ITimelineSummary): ICameraFootageTimelineDisplaySection[] {
        let availableFootage: ICameraFootageTimelineDisplaySection[] = [];

        if (!timeline?.displaySections?.length) {
            return;
        }

        availableFootage = timeline.displaySections.filter(footage => {
            const endOfToday = this.dateService.getEndOfDay(new Date());
            return (!!footage.dateEnd)
                ? this.dateService.dateIsBetweenDates(time, footage.dateStart, footage.dateEnd, true)
                : this.dateService.dateIsBetweenDates(time, footage.dateStart, endOfToday, true)
        });

        return availableFootage;
    }

    // ---

    private createTimelineFootage(requestDate: Date, historicalFootage: IAvailableDailyFootage[] | IJourney[], startKey: string, endKey: string, label: string = ""): IAvailableVideoFootageSection[] {
        const newTimelineFootage: IAvailableVideoFootageSection[] = [];
        const requestDateMoment = this.veriDate.createMoment(requestDate);

        historicalFootage.forEach((footageHistory, index) => {
            
            let footageStartTime = (footageHistory[startKey]) ? this.veriDate.createMoment(footageHistory[startKey]) : null;
            let footageEndTime = (footageHistory[endKey]) ? this.veriDate.createMoment(footageHistory[endKey]) : null;

            if (!footageHistory[endKey]) {
                footageEndTime = this.veriDate.createMoment(new Date());
            }

            if (!footageStartTime.isSame(requestDateMoment, "day") && footageEndTime.isSame(requestDateMoment, "day")) {
                footageStartTime = footageEndTime.clone().startOf("day");

            } else if (!footageEndTime.isSame(requestDateMoment, "day") && footageStartTime.isSame(requestDateMoment, "day")) {
                footageEndTime = footageStartTime.clone().endOf("day");
            }

            const existingMatchingStartSection = newTimelineFootage.find(section => {
                return Math.abs(section.startTime.diff(footageStartTime) / 1000) <= 10;
            });

            if (existingMatchingStartSection) {

                if (footageStartTime.isAfter(existingMatchingStartSection.startTime)) {
                    existingMatchingStartSection.startTime = footageStartTime;
                }

                if (footageEndTime.isBefore(existingMatchingStartSection.endTime)) {
                    existingMatchingStartSection.endTime = footageEndTime;
                }

                existingMatchingStartSection.historicalFootage.push(footageHistory);

                if (VideoStreamType[footageHistory.StreamType] && !existingMatchingStartSection.videoStreamTypes.includes(footageHistory.StreamType)) {
                    existingMatchingStartSection.videoStreamTypes.push(footageHistory.StreamType);
                }

                existingMatchingStartSection.hasMainStream = (existingMatchingStartSection.hasMainStream) ? true : footageHistory.StreamType === VideoStreamType.Main;
                existingMatchingStartSection.hasSubStream = (existingMatchingStartSection.hasSubStream) ? true : footageHistory.StreamType === VideoStreamType.Sub

                return;
            }

            const footageSection: IAvailableVideoFootageSection = {
                startTime: footageStartTime,
                endTime: footageEndTime,
                startPercentage: 0,
                widthPercentage: 0,
                label: label,
                historicalFootage: [footageHistory],
                videoStreamTypes: [],
                hasMainStream: footageHistory.StreamType === VideoStreamType.Main,
                hasSubStream: footageHistory.StreamType === VideoStreamType.Sub
            };

            if (VideoStreamType[footageHistory.StreamType]) {
                footageSection.videoStreamTypes.push(footageHistory.StreamType);
            }

            newTimelineFootage.push(footageSection);
        });

        return newTimelineFootage;
    }
}
